import { KalturaAssetType } from '@kaltura-ott/ott-client-types';
import {
  getAggregatedKsql,
  getAggregatedKsqlForPersons,
  getSearchHistoryList,
  KuxFilteredCategoriesDTO,
  SearchKeys,
  setSearchHistoryList,
  SpecialWidgetTypeEnum,
  UIDAPresentationTypeEnum,
  UIDAStructureTypeEnum,
} from '@kaltura-ott/tvpil';
import { UserSearchEventArgs } from '@kaltura-ott/tvpil-analytics';
import { action, computed, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container } from 'tsyringe';

import { assertPresence, errorHandling } from '../../utils/error-handling';
import { AppParametersStore } from '../app/app-params-store';
import type { AppStore } from '../app/app-store';
import { CollectionRailStoreV2 } from '../category/widgets/rails/index';
import { DmsStore } from '../dms';
import { Program } from '../entities';
import { creatorEntityStore } from '../entities/entities.service';
import { MediaAsset } from '../entities/MediaAsset';
import { ErrorPriority } from '../error';
import { TransactionStore } from '../transaction/transaction-store';

import { ParentalPinStore } from './../parentalPin';
import { ProvidersRepository } from './../providersRepository/index';
import { createSearchGridRail, isExistsInSearchHistory } from './search-page-helper';

const MIN_SEARCH_STRING_LENGTH = 3;

/**
 * @category Store
 */

export interface SearchHistoryAsset {
  title: string;
  id: number;
}

export class SearchStore {
  static moduleName = 'SearchStore';

  type: UIDAStructureTypeEnum.page = UIDAStructureTypeEnum.page;
  subtype: UIDAPresentationTypeEnum.search = UIDAPresentationTypeEnum.search;

  @observable isReady = false;
  @observable private fullHistoryList: MediaAsset[] = [];
  @observable railStore?: CollectionRailStoreV2;
  @observable castRail?: CollectionRailStoreV2;
  @observable searchString: string | undefined;
  @observable isInitiated = false;

  public link: string;

  private readonly maxPersonalListCount: number;
  private readonly searchAttributes: SearchKeys[] | SearchKeys;
  private readonly searchTree: KuxFilteredCategoriesDTO;
  private dmsStore: DmsStore;
  private readonly isWeb: boolean;
  private debounceTime?: NodeJS.Timeout;
  private readonly endSearchTime: number;
  private isSearchFailed = false;
  private showCastRail: boolean;

  recommendationsStore: CollectionRailStoreV2 | undefined;
  endVoiceSearchTime: number;
  uiDesignElements: KuxFilteredCategoriesDTO['uiDesignElements'];

  constructor(searchTree: KuxFilteredCategoriesDTO) {
    this.dmsStore = container.resolve<DmsStore>('dmsStore');
    this.searchTree = searchTree;
    this.endSearchTime = searchTree.searchProperty.endSearchTimeInMilliseconds;
    this.uiDesignElements = searchTree.uiDesignElements;
    this.maxPersonalListCount = this.searchTree.searchProperty.maxNumbersOfHistorySearchList;
    this.searchAttributes = this.searchTree.searchProperty.searchMainDiscoveryAttributes;
    this.endVoiceSearchTime = this.dmsStore.dms.temporaryParams.endVoiceSearchTimeInSec * 1000;
    this.showCastRail = this.dmsStore.dms.showCastAndCrewInSearch;

    this.link = `/page/${searchTree.referenceId}`;

    const appParametersStore = container.resolve<AppParametersStore>('appParamsStore');
    this.isWeb = appParametersStore.params?.dmsSetting?.platform === 'other';
  }

  @action
  @errorHandling({ type: ErrorPriority.low })
  async init(): Promise<void> {
    const parentalPinStore = container.resolve<ParentalPinStore>('parentalPinStore');
    const transactionStore = container.resolve<TransactionStore>('transactionStore');

    parentalPinStore.setLastPageLink('');

    await this.loadSearchHistoryList();

    if (!this.isWeb && this.searchTree.searchProperty.recommendationsCollectionId) {
      this.recommendationsStore = createSearchGridRail(this.searchTree, SpecialWidgetTypeEnum.recommendations);
      this.railStore = this.recommendationsStore;
      await this.railStore.init();
    }
    await transactionStore.initSubscriptions();

    this.isInitiated = true;
    this.isReady = true;
  }

  @action
  @errorHandling()
  async search(searchString?: string, searchNow = false): Promise<void> {
    if (!this.searchTree.channels?.[0]) {
      return;
    }

    if (this.railStore?.specialWidgetType === SpecialWidgetTypeEnum.recommendations) {
      this.isReady = false;
      this.railStore = undefined;
    }

    const newSearchString = searchString?.toLowerCase().trimStart().replace(/\s+/g, ' ');

    if (newSearchString && this.searchString?.toLowerCase() === newSearchString && !this.isSearchFailed) {
      if (newSearchString.length >= MIN_SEARCH_STRING_LENGTH) {
        this.searchString = searchString?.trimStart().replace(/\s+/g, ' ');
        this.isReady = true;
      }

      return;
    } else {
      if (this.debounceTime) {
        clearTimeout(this.debounceTime);
      }
    }
    this.searchString = searchString?.trimStart().replace(/\s+/g, ' ');

    if (searchNow) {
      await this.loadSearchResults();
      this.isReady = true;
      this.isSearchFailed = false;
    } else {
      this.debounceTime = setTimeout(async () => {
        await this.loadSearchResults();
        this.isReady = true;
        this.isSearchFailed = false;
      }, this.endSearchTime);
    }

    if (this.searchString?.length === 0) {
      if (this.isWeb) {
        this.railStore = undefined;
      } else {
        this.railStore = this.recommendationsStore;
      }
      this.isSearchFailed = false;
    }
  }

  private handleSearchError = () => {
    this.isSearchFailed = true;
  };

  @action
  private async loadSearchResults(): Promise<void> {
    this.searchTree.groupBy = this.dmsStore.dms.searchCharacteristics.GROUP_BY_VALUE;
    this.searchTree.railProperty.isGroupedGrid =
      !!this.dmsStore.dms.searchCharacteristics.GROUP_BY_VALUE || this.searchTree.searchProperty.isGroupByEnabled;
    if (
      !this.searchString ||
      this.searchAttributes.length === 0 ||
      !(this.searchString?.length >= MIN_SEARCH_STRING_LENGTH)
    ) {
      return;
    }

    const providerRepo = container.resolve<ProvidersRepository>('providersRepository');

    const ksql = getAggregatedKsql({
      searchString: this.searchString,
      searchAttributes: this.searchAttributes,
      kSql: this.searchTree.aggregatedKSQL,
      providerKsql: providerRepo.searchKsql,
    });

    const innerRail = createSearchGridRail(this.searchTree, SpecialWidgetTypeEnum.search, ksql, this.handleSearchError);
    await innerRail.init();
    this.railStore = innerRail;

    const personsKsql = getAggregatedKsqlForPersons({
      searchString: this.searchString,
      assetTypes: assertPresence(this.dmsStore.dms).mediaTypes,
      kSql: this.searchTree.aggregatedKSQL,
    });

    if (this.showCastRail) {
      this.castRail = createSearchGridRail(this.searchTree, SpecialWidgetTypeEnum.cast, personsKsql);
      await this.castRail.init();
    }

    if (this.dmsStore.dms.analytics?.events?.allowDeviceUserActionEvents) {
      const appStore = container.resolve<AppStore>('appStore');
      appStore.kuxAnalytics?.send(new UserSearchEventArgs(this.searchString));
    }
  }

  @computed
  public get historyList(): SearchHistoryAsset[] {
    return this.fullHistoryList.slice(0, this.maxPersonalListCount).map((item) => {
      return {
        title: item.data.title,
        id: item.data.id,
      };
    });
  }

  @action
  private async loadSearchHistoryList(): Promise<void> {
    const items = await lastValueFrom(getSearchHistoryList(this.dmsStore.dms.chosenSearchList));
    this.fullHistoryList = items.map((item) => creatorEntityStore(item));
  }

  @errorHandling({ type: ErrorPriority.low })
  public async addToSearchHistory<T extends MediaAsset>(asset: T): Promise<void> {
    if (isExistsInSearchHistory(asset, this.fullHistoryList, this.maxPersonalListCount)) {
      return;
    }
    await lastValueFrom(
      setSearchHistoryList(
        asset.id,
        asset instanceof Program ? KalturaAssetType.epg : KalturaAssetType.media,
        this.dmsStore.dms.chosenSearchList,
      ),
    );
    await this.loadSearchHistoryList();
  }

  public setRecommendations(): void {
    if (this.searchTree.searchProperty.recommendationsCollectionId) {
      this.railStore = this.recommendationsStore;
    }
  }

  @action
  resetStore() {
    this.isReady = false;
    this.isInitiated = false;
    this.searchString = undefined;
    this.railStore = undefined;
    this.castRail = undefined;
    this.isSearchFailed = false;
  }
}
